import * as React from 'react';
import { IData } from '../virtual-data/VirtualData';
import {
  DEFAULT_FIELD_VALUES_FILTER_MAX_FETCHED_ITEMS,
  FinalTableComputedColumn,
  FinalTableProps,
} from './FinalTableProps';
import { GridCellProps } from 'react-virtualized/dist/es/Grid';
import * as Classes from '../common/classes';
import classNames from 'classnames';
import { ColumnFilter } from './ColumnFilter';
import { ColumnSort } from './ColumnSort';
import { MouseEvent } from 'react';
import { Icon } from '../icon/Icon';
import { mcGroup, mcUngroup } from '@mc/react-ui-icons';
import {
  getColumnField,
  getColumnFieldNoCheck,
  isColumnSupportsFilter,
} from './utils';
import { ColumnResizer } from './ColumnResizer';

type Props<DATA extends IData<ID>, FILTER, ID> = {
  finalTableProps: FinalTableProps<DATA, FILTER, ID>;
  formattedColumns: FinalTableComputedColumn<DATA>[];
  columnResizing: boolean;
  gridCellProps: GridCellProps;
  setColumnResize: (
    finalTableProps: FinalTableProps<DATA, FILTER, ID>,
    formattedColumns: FinalTableComputedColumn<DATA>[],
    position: number,
    identifikator: string,
    widths: { [key: number]: number }
  ) => void;
  columnsWidth: { [key: number]: number };
};

export const HeaderCell = <DATA extends IData<ID>, FILTER, ID>({
  finalTableProps,
  formattedColumns,
  gridCellProps,
  setColumnResize,
  columnsWidth,
  columnResizing,
}: Props<DATA, FILTER, ID>) => {
  // Scroolbar width column
  if (gridCellProps.columnIndex === formattedColumns.length) {
    return (
      <div
        key={gridCellProps.key}
        className={Classes.FINAL_TABLE_HEADER_CELL}
        style={gridCellProps.style}
      ></div>
    );
  }

  const column = formattedColumns[gridCellProps.columnIndex];

  let cell;
  if (column.check) {
    cell = (
      <CheckContentCell finalTableProps={finalTableProps} column={column} />
    );
  } else if (column.group) {
    cell = (
      <GroupContentCell finalTableProps={finalTableProps} column={column} />
    );
  } else {
    cell = (
      <NormalContentCell finalTableProps={finalTableProps} column={column} />
    );
  }

  return (
    <div
      key={gridCellProps.key}
      style={gridCellProps.style}
      className={classNames(Classes.FINAL_TABLE_HEADER_CELL, {})}
    >
      {cell}
      {column.type !== 'action' &&
        column.type !== '_check' &&
        (!finalTableProps?.errors || finalTableProps?.errors?.length < 1) && (
          <ColumnResizer
            columnResizing={columnResizing}
            setColumnResize={(e: MouseEvent) => {
              let field = getColumnFieldNoCheck(column);
              if (field) {
                setColumnResize(
                  finalTableProps,
                  formattedColumns,
                  e.clientX,
                  field,
                  columnsWidth
                );
              }
            }}
          />
        )}
    </div>
  );
};

type CellProps<DATA extends IData<ID>, FILTER, ID> = {
  finalTableProps: FinalTableProps<DATA, FILTER, ID>;
  column: FinalTableComputedColumn<DATA>;
};

export const CheckContentCell = <DATA extends IData<ID>, FILTER, ID>({
  column,
}: CellProps<DATA, FILTER, ID>) => {
  return (
    <div className={classNames(Classes.FINAL_TABLE_HEADER_CELL_CHECK)}>
      {column.header}
    </div>
  );
};

export const NormalContentCell = <DATA extends IData<ID>, FILTER, ID>({
  finalTableProps,
  column,
}: CellProps<DATA, FILTER, ID>) => {
  getColumnFieldNoCheck(column);
  const sortInfo = finalTableProps.getSortInfo(column);
  const hasFilters =
    (finalTableProps.config.columnsFilterable || column.filterable === true) &&
    isColumnSupportsFilter(column);

  const showDepth = finalTableProps.getColumnsSortInfo().length > 1;

  let handleSort =
    column.sortable && !finalTableProps.loading
      ? (e: MouseEvent<HTMLDivElement>) => {
          document.getSelection()?.removeAllRanges();
          e.stopPropagation();
          e.preventDefault();
          finalTableProps.toggleSort(column, e.shiftKey);
        }
      : undefined;

  return (
    <div
      className={classNames(Classes.FINAL_TABLE_HEADER_CELL_NORMAL, {
        [Classes.FINAL_TABLE_SORTABLE]: column.sortable,
      })}
      title={
        column.headerTitle ||
        (typeof column.header === 'string' ? column.header : undefined)
      }
      onClick={handleSort}
    >
      <div className={classNames(Classes.FINAL_TABLE_HEADER_CELL_VALUE, {})}>
        {column.header}
      </div>
      {column.sortable && (
        <ColumnSort sortInfo={sortInfo} showDepth={showDepth} />
      )}
      {(column.groupable || hasFilters) && (
        <div
          className={classNames(
            Classes.FINAL_TABLE_HEADER_ACTIONS,
            Classes.FINAL_TABLE_HEADER_ACTIONS_FLOATING
          )}
        >
          {column.groupable && (
            <div
              className={classNames(Classes.FINAL_TABLE_HEADER_ICON_ACTION)}
              title={'Seskupit dle sloupce'}
              onClick={() => {
                const f = getColumnField(column);
                finalTableProps.addGroup(f);
              }}
            >
              <Icon icon={mcGroup} />
            </div>
          )}
          {hasFilters && (
            <ColumnFilter<DATA, ID>
              renderIfNotFilled={true}
              column={column}
              fieldValuesFilter={finalTableProps.fieldValuesFilter}
              setFieldValuesFilter={finalTableProps.setFieldValuesFilter}
              searchFieldValues={finalTableProps.searchFieldValues}
              fieldValuesFilterMaxFetchedItems={
                finalTableProps.config.fieldValuesFilterMaxFetchedItems ||
                DEFAULT_FIELD_VALUES_FILTER_MAX_FETCHED_ITEMS
              }
            />
          )}
        </div>
      )}
      {hasFilters && (
        <div className={Classes.FINAL_TABLE_HEADER_ACTIONS}>
          <ColumnFilter<DATA, ID>
            renderIfNotFilled={false}
            column={column}
            fieldValuesFilter={finalTableProps.fieldValuesFilter}
            setFieldValuesFilter={finalTableProps.setFieldValuesFilter}
            searchFieldValues={finalTableProps.searchFieldValues}
            fieldValuesFilterMaxFetchedItems={
              finalTableProps.config.fieldValuesFilterMaxFetchedItems ||
              DEFAULT_FIELD_VALUES_FILTER_MAX_FETCHED_ITEMS
            }
          />
        </div>
      )}
    </div>
  );
};

export const GroupContentCell = <DATA extends IData<ID>, FILTER, ID>({
  finalTableProps,
  column,
}: CellProps<DATA, FILTER, ID>) => {
  return (
    <div className={classNames(Classes.FINAL_TABLE_HEADER_CELL_GROUP, {})}>
      {column.groupColumns?.map((innerColumn, innerColumnIndex) => {
        return (
          <Group<DATA, FILTER, ID>
            key={innerColumnIndex}
            depth={innerColumnIndex}
            finalTableProps={finalTableProps}
            column={innerColumn}
          />
        );
      })}
    </div>
  );
};

type GroupProps = {
  depth: number;
};

const Group = <DATA extends IData<ID>, FILTER, ID>({
  finalTableProps,
  column,
  depth,
}: CellProps<DATA, FILTER, ID> & GroupProps) => {
  const sortInfo = finalTableProps.getSortInfo(column);
  const hasFilters =
    (finalTableProps.config.columnsFilterable || column.filterable === true) &&
    isColumnSupportsFilter(column);

  let handleSort =
    column.sortable && !finalTableProps.loading
      ? (e: MouseEvent<HTMLDivElement>) => {
          document.getSelection()?.removeAllRanges();
          e.stopPropagation();
          e.preventDefault();
          finalTableProps.toggleSort(column, e.shiftKey);
        }
      : undefined;

  return (
    <div
      className={classNames(Classes.FINAL_TABLE_GROUP, `depth-${depth}`, {
        [Classes.FINAL_TABLE_SORTABLE]: column.sortable,
      })}
      title={
        column.headerTitle ||
        (typeof column.header === 'string' ? column.header : undefined)
      }
      onClick={handleSort}
    >
      <div className={classNames(Classes.FINAL_TABLE_HEADER_CELL_VALUE, {})}>
        {column.header}
      </div>
      {column.sortable && <ColumnSort sortInfo={sortInfo} showDepth={false} />}
      <div className={Classes.FINAL_TABLE_HEADER_ACTIONS}>
        {hasFilters && (
          <ColumnFilter<DATA, ID>
            column={column}
            fieldValuesFilter={finalTableProps.fieldValuesFilter}
            setFieldValuesFilter={finalTableProps.setFieldValuesFilter}
            searchFieldValues={finalTableProps.searchFieldValues}
            fieldValuesFilterMaxFetchedItems={
              finalTableProps.config.fieldValuesFilterMaxFetchedItems ||
              DEFAULT_FIELD_VALUES_FILTER_MAX_FETCHED_ITEMS
            }
          />
        )}

        <div
          className={classNames(Classes.FINAL_TABLE_HEADER_ICON_ACTION)}
          title={'Zrušit seskupení'}
          onClick={() => {
            let field = getColumnFieldNoCheck(column);
            if (field) {
              finalTableProps.removeGroup(field);
            }
          }}
        >
          <Icon icon={mcUngroup} fixedWidth />
        </div>
      </div>
    </div>
  );
};
